import React, { useEffect, useState } from 'react';
import { SectionSelector } from '../../../myAudit/components/SectionSelector';
import { ISectionSelectorOptions } from '../../../@types/sectionSelector';
import {
  createMyAudit,
  getMyAuditTemplateCategories,
} from '../../../../requests/myAudit';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../helpers/showMessage';
import { ButtonBlockColumn } from './../../../common/sharedStyledComponents/GenericStyles.styled';
import { getItem, setItem } from '../../../../utils/localStorage';
import { castSelectedDateWithoutConversion } from '../../../../helpers/dates';
import { ButtonLarge } from '../../../common/sharedComponents/buttons/ButtonLarge';
import { useTranslation } from 'react-i18next';
import { Headline3 } from '../../../common/typography';
import {
  ButtonContainer,
  HeaderContainer,
  StyledMyAuditContainer,
} from '../../../common/sharedStyledComponents/MyAuditGenericStyles.styled';
import { AppHeader } from '../../../common/sharedComponents/AppHeader';
import { sentryReport } from '../../../../helpers/sentry';
import { SectionsSkeleton } from './../../MyAuditSkeleton/SectionsSkeleton';
import UnsavedFormDataModal from '../../../../components/commons/UnsavedFormDataModal';
import {
  IMyAuditPayload,
  IMyAuditPreviewCard,
  IMyAuditSectionData,
  IMyAuditSectionResponse,
} from '../../../@types/myAudit';
import { Nullable } from '../../../@types/genericTypes';

export function MyAuditSections () {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location: any = useLocation();
  const { templateName, myAuditPreview, currentAuditorIndex } = location.state;
  const userData = getItem('user');
  const myAudit: IMyAuditPayload = getItem('myAudit');
  const currentSectionSelection =
    myAudit.sections.length > 0
      ? myAudit.sections.map((section) => section.templateSectionId)
      : [];
  const [sectionsIds, setSectionsIds] = useState<Nullable<string | number>[]>(
    []
  );
  const [sectionSelectorOptions, setSectionSelectorOptions] = useState<
    ISectionSelectorOptions[]
  >([]);
  const [sectionsData, setSectionsData] = useState<IMyAuditSectionData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAllSectionsSelected, setIsAllSectionsSelected] =
    useState<boolean>(false);
  const [showUnsavedDataModal, setShowUnsavedDataModal] =
    useState<boolean>(false);

  const myAuditPreviewCardsInitialState: IMyAuditPreviewCard[] = [
    {
      auditor: {
        id: 0,
        text: '',
      },
      sectionsCard: {
        names: [],
        dueDate: '',
      },
    },
  ];

  const myAuditPreviewCards: IMyAuditPreviewCard[] =
    myAuditPreview && myAuditPreview.length > 0
      ? myAuditPreview
      : myAuditPreviewCardsInitialState;

  const filteredSectionsData = () => {
    return sectionsData.filter((section) =>
      sectionsIds.includes(section.templateSectionId)
    );
  };

  const setMyAuditPreview = () => {
    const index = currentAuditorIndex || 0;

    if (myAuditPreviewCards[index - 1]) {
      myAuditPreviewCards[index] = {
        sectionsCard: {
          names: filteredSectionsData().map((section) => {
            return section.sectionName!;
          }),
          dueDate: myAuditPreviewCards[index - 1].sectionsCard!.dueDate,
        },
      };
    }

    myAuditPreviewCards[index].sectionsCard!.names = filteredSectionsData().map(
      (section) => {
        return section.sectionName!;
      }
    );
    myAuditPreviewCards[index].sectionsCard!.dueDate = myAudit.sections.map(
      (section) => section.dueDate
    )[0];
    myAuditPreviewCards[index].templateName = templateName;
  };

  const handleAssignSections = () => {
    if (!sectionsIds.length) {
      showErrorMessage(t('COMMON.MY_AUDIT.SECTIONS.ERROR_SELECT_SECTION'));
      return;
    }

    myAudit.sections = sectionsIds.map((sectionId) => {
      return {
        templateSectionId: sectionId,
        auditorId: null,
        dueDate: '',
        notes: '',
      };
    });
    setMyAuditPreview();
    navigate(`/myaudit/assign`, {
      state: {
        templateName,
        myAuditPreview: myAuditPreviewCards,
        sectionsData: filteredSectionsData(),
        isAllSectionsSelected,
        currentAuditorIndex,
      },
    });
  };

  const handlePreviewMyAudit = () => {
    setMyAuditPreview();
    const filteredPreviewCards = myAuditPreviewCards.filter(
      (auditorCard) => auditorCard.auditor
    );
    navigate('/myaudit/preview', {
      state: {
        myAuditPreview: filteredPreviewCards,
        templateName,
      },
    });
  };

  const setSelectAllState = (selectionOptions: ISectionSelectorOptions[]) => {
    const allSelectedExceptSelectAll = selectionOptions
      .slice(1) // Exclude "Select All" checkbox
      .every((option: ISectionSelectorOptions) =>
        [...sectionsIds, ...currentSectionSelection].includes(option.id)
      );
    setIsAllSectionsSelected(allSelectedExceptSelectAll);
    // Update the state of the "Select All" checkbox
    setSectionSelectorOptions((options) =>
      options.map((option, index) => {
        if (index === 0) {
          return {
            ...option,
            checked: allSelectedExceptSelectAll,
          };
        }
        return option;
      })
    );
  };

  const convertToSectionData = (sections: IMyAuditSectionResponse[]) => {
    setSectionsData(
      sections.map((section) => {
        return {
          templateSectionId: section.id,
          sectionName: section.value,
          auditorId: null,
          dueDate: myAudit.sections.map((section) => section.dueDate)[0] || '',
          notes: '',
        };
      })
    );
  };

  useEffect(() => {
    setSectionsData([]);
    if (myAudit.templateId) {
      getMyAuditTemplateCategories(myAudit.templateId)
        .then((response) => {
          const sectionsResponse: IMyAuditSectionResponse[] = response.data;
          if (sectionsResponse && sectionsResponse.length) {
            convertToSectionData(sectionsResponse);
            const selectionOptions: ISectionSelectorOptions[] = [
              {
                label: `${t('COMMON.MY_AUDIT.TITLES.SELECT_ALL')}`,
                optionCallback: handleSelectAll,
                id: 'all',
                checked: false,
                disabled: false,
              },
            ];
            sectionsResponse.forEach((section: IMyAuditSectionResponse) => {
              selectionOptions.push({
                label: section.value,
                optionCallback: () => handleCheckbox(section.id),
                id: section.id,
                checked: currentSectionSelection.includes(section.id),
                disabled: currentSectionSelection.includes(section.id),
              });
            });
            setSectionSelectorOptions(selectionOptions);

            if (selectionOptions.length > 1) {
              setSelectAllState(selectionOptions);
            }
          }
        })
        .catch((error) => {
          sentryReport(error.response.data, error.message, error.code, {
            summaryError: t(
              'COMMON.ALERT_MESSAGES.UNABLE_TO_GET_SECTIONS_LIST'
            ),
            path: '/auth/me',
          });
          showErrorMessage(
            t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_SECTIONS_LIST')
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    if (sectionSelectorOptions.length > 1) {
      setSelectAllState(sectionSelectorOptions);
    }
  }, [sectionsIds]);

  const handleSelectAll = () => {
    setSectionSelectorOptions((prevOptions) => {
      const isAllChecked =
        isAllSectionsSelected ||
        prevOptions.every((option) => option.checked || option.disabled);
      const updatedOptions = prevOptions.map((option) => ({
        ...option,
        checked: !currentSectionSelection.includes(option.id)
          ? !isAllChecked
          : option.checked,
      }));
      const selectedSectionIds = isAllChecked
        ? []
        : updatedOptions
            .slice(1)
            .filter((item) => !currentSectionSelection.includes(item.id))
            .map((item) => item.id);
      setSectionsIds(selectedSectionIds);
      return updatedOptions;
    });
  };

  const handleCheckbox = (selectedOptionId: string | number) => {
    setSectionSelectorOptions((previousOptions) => {
      const updatedOptions = previousOptions.map((option) => {
        if (option.id === selectedOptionId) {
          return {
            ...option,
            checked: !option.checked,
          };
        }
        return option;
      });
      return updatedOptions;
    });
    setSectionsIds((previousSectionsIds) => {
      const isSectionChecked = previousSectionsIds.some(
        (sectionId) => sectionId === selectedOptionId
      );
      if (isSectionChecked) {
        const selection = previousSectionsIds.filter(
          (sectionId) => sectionId !== selectedOptionId
        );
        return [...selection];
      } else {
        return [...previousSectionsIds, selectedOptionId];
      }
    });
  };

  const handleQuickMyAudit = () => {
    setItem('isFormData', false);
    const payload: IMyAuditPayload = {
      templateId: myAudit.templateId,
      subject: `Quick myAudit - ${templateName}`,
      hotelId: getItem('selectedHotel'),
      sections: sectionsIds.map((section) => {
        return {
          templateSectionId: section,
          auditorId: userData.user.id,
          dueDate: castSelectedDateWithoutConversion(new Date(), 'YYYY-MM-DD'),
          notes: `Quick myAudit - ${templateName}`,
        };
      }),
    };
    createMyAudit(payload)
      .then((response) => {
        showSuccessMessage(t('COMMON.MY_AUDIT.ASSIGN_FORM.SUCCESS'));
        navigate(`/myaudit/conduct?auditId=${response.data}`);
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          summaryError: t('COMMON.ALERT_MESSAGES.UNABLE_TO_CREATE_MYAUDIT'),
          path: '/myAudit',
        });
        showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_CREATE_MYAUDIT'));
      });
  };

  const handleBackButton = () => {
    if (myAudit.sections.length > 0) {
      setShowUnsavedDataModal(true);
      return;
    }
    navigate('/myaudit/checklist');
  };

  return (
    <>
      <HeaderContainer>
        <AppHeader
          title={t('COMMON.MY_AUDIT.TITLE')}
          onClick={handleBackButton}
        />
      </HeaderContainer>
      <StyledMyAuditContainer>
        <Headline3>{t('COMMON.MY_AUDIT.TITLES.SELECT_SECTIONS')}</Headline3>
        {isLoading ? (
          <SectionsSkeleton />
        ) : (
          <>
            <SectionSelector
              title={templateName}
              options={sectionSelectorOptions}
            />
            <ButtonContainer>
              <ButtonBlockColumn>
                <ButtonLarge
                  outlined={true}
                  title={t('COMMON.MY_AUDIT.BUTTON.ASSIGN_SECTIONS')}
                  backgroundColor="oxfordBlue"
                  height="h6"
                  onClick={handleAssignSections}
                  disabled={sectionsIds.length === 0}
                />
                {myAudit && myAudit.sections.length ? (
                  <ButtonLarge
                    outlined={true}
                    title={t('COMMON.MY_AUDIT.BUTTON.FINALISE')}
                    backgroundColor="verdigris"
                    textColor="oxfordBlue"
                    height="h6"
                    onClick={handlePreviewMyAudit}
                    disabled={sectionsIds.length > 0}
                  />
                ) : (
                  <ButtonLarge
                    outlined={true}
                    title={t('COMMON.MY_AUDIT.BUTTON.QUICK_MY_AUDIT')}
                    backgroundColor="verdigris"
                    textColor="oxfordBlue"
                    height="h6"
                    onClick={handleQuickMyAudit}
                    disabled={
                      sectionsIds.length === 0 ||
                      (myAudit && myAudit.sections.length > 0)
                    }
                  />
                )}
                <ButtonLarge
                  outlined={true}
                  inverted={true}
                  backgroundColor="eerieBlack"
                  title={t('COMMON.MY_AUDIT.BUTTON.BACK')}
                  onClick={handleBackButton}
                />
              </ButtonBlockColumn>
            </ButtonContainer>
          </>
        )}
      </StyledMyAuditContainer>
      {showUnsavedDataModal && (
        <UnsavedFormDataModal
          showUnsavedDataModal={showUnsavedDataModal}
          setShowUnsavedDataModal={setShowUnsavedDataModal}
          onContinue={() => navigate('/myaudit/checklist')}
        />
      )}
    </>
  );
}
